import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { redirectToBackofficeV1 } from '@/utils/backoffice-v1';
import { getUseGuestHierarchy } from '@/redux/launchDarkly';
import { getUserToken } from '@/redux/user';
import { useLazyGetCustomerListForFullExportQuery } from '@/services/innovorder/customer/customer.endpoints';
import { useGetBrandQuery, useGetBrandOptInsQuery } from '@/services/innovorder/brand/brand.endpoints';
import { BrandOptIn } from '@/services/innovorder/brand/brand.type';
import { XlsxFile } from '@/utils/xslx';
import { downloadFile } from '@/utils/file/utils';
import { CustomerForExport } from '@/services/innovorder/customer/customer.types';

const formatDate = (date: string | Date | null | undefined): string => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('fr-FR');
};

const centsToEuros = (value: number | string | null | undefined): number => {
    if (value === null || value === undefined) return 0;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return 0;
    return num / 100;
};

const buildExportHeaders = (
    options: { educationMode: boolean; tripAdvisor: boolean; birthdate: boolean },
    brandOptIns: BrandOptIn[],
): string[] => {
    const headers = [
        'Identifiant client',
        'Email',
        'Prénom',
        'Nom',
        'Numéro de téléphone',
        'Total dépensé',
        'Nombre de commandes',
        'Panier moyen',
        'Dernière commande',
        'Fréquence',
        'Statut',
    ];

    if (options.tripAdvisor) {
        headers.push("Nom d'utilisateur TripAdvisor");
    }

    if (options.educationMode) {
        headers.push(
            'Solde',
            'N° badge',
            'N° convive',
            'Groupe',
            'Section',
            'Sous-section',
            'Type de compte',
            'Code tarif',
            'Code subvention',
            'Code admission',
            'Code régime tarifaire',
            "Date d'archivage",
            'Archivé',
            "Date d'arrivée",
            'Date de départ',
            'Badge bloqué',
        );
    }

    for (const optIn of brandOptIns) {
        headers.push(optIn.text);
    }

    if (options.birthdate) {
        headers.push("Date d'anniversaire");
    }

    headers.push('Créé le');

    return headers;
};

const buildExportRow = (
    customer: CustomerForExport,
    options: { educationMode: boolean; tripAdvisor: boolean; birthdate: boolean },
    brandOptIns: BrandOptIn[],
): (string | number)[] => {
    const row: (string | number)[] = [
        customer.customerId ?? '',
        customer.email ?? '',
        customer.firstName ?? '',
        customer.lastName ?? '',
        customer.phone ?? '',
        centsToEuros(customer.totalSpent),
        customer.totalOrders ?? 0,
        centsToEuros(customer.averagePurchase),
        formatDate(customer.lastOrder),
        customer.averageOrderPerWeek ?? '',
        customer.disabledAt ? 'Inactif' : 'Actif',
    ];

    if (options.tripAdvisor) {
        row.push(customer.tripAdvisorUsername ?? '');
    }

    if (options.educationMode) {
        const accountPaymentType =
            customer.accountType === 'BANKDEBIT' ? 'BANKDEBIT' : customer.accountPaymentType ?? '';
        row.push(
            centsToEuros(customer.balance),
            customer.badgeNumber ?? '',
            customer.studentNumber ?? '',
            customer.class ?? '',
            customer.section ?? '',
            customer.subSection ?? '',
            accountPaymentType,
            customer.tariffCode ?? '',
            customer.grantCode ?? '',
            customer.entranceFeeCode ?? '',
            customer.pricingRuleCode ?? '',
            formatDate(customer.archivedAt),
            customer.archivedAt && new Date(customer.archivedAt) < new Date() ? 'oui' : 'non',
            formatDate(customer.accessFrom),
            formatDate(customer.accessUntil),
            customer.isBadgeBlocked ? 'oui' : 'non',
        );
    }

    for (const optIn of brandOptIns) {
        row.push(customer.optIns && customer.optIns[optIn.optInId] ? 1 : 0);
    }

    if (options.birthdate) {
        row.push(formatDate(customer.birthdate));
    }

    // API returns createdAt (camelCase) due to Sequelize underscored:true
    const createdAt = customer.created_at ?? (customer as Record<string, unknown>).createdAt;
    row.push(formatDate(createdAt as string));

    return row;
};

export type CustomerTableViewModel = {
    onHideCreateModal: () => void;
    handleImport: () => void;
    handleExportXlsx: () => void;
    handleExportCsv: () => void;
    shouldUseGuestHierarchy: boolean;
    showImportModal: boolean;
    handleCloseImportModal: () => void;
    isExporting: boolean;
};

export const useCustomersListVM = ({ brandId }: { brandId: number }): CustomerTableViewModel => {
    const shouldUseGuestHierarchy = useSelector(getUseGuestHierarchy);
    const token = useSelector(getUserToken);
    const [showImportModal, setShowImportModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [getCustomerListForFullExport] = useLazyGetCustomerListForFullExportQuery();
    const { data: brand } = useGetBrandQuery({ brandId, token });
    const { data: brandOptIns } = useGetBrandOptInsQuery({ brandId, token });

    const getBrandFlags = useCallback(() => {
        const opts = brand?.options;
        return {
            educationMode: !!opts?.EDUCATION_MODE?.isActivated,
            tripAdvisor: !!opts?.TRIP_ADVISOR_USERNAME?.isActivated,
            birthdate: !!opts?.BIRTHDATE?.isActivated,
        };
    }, [brand]);

    const onHide = useCallback(() => {
        redirectToBackofficeV1('/home/customers');
    }, []);

    const handleImport = useCallback(() => {
        setShowImportModal(true);
    }, []);

    const handleCloseImportModal = useCallback(() => {
        setShowImportModal(false);
    }, []);

    const fetchCustomersForExport = useCallback(async () => {
        const result = await getCustomerListForFullExport({ brandId, token }).unwrap();
        const customers = Array.isArray(result) ? result : (result as { customers: CustomerForExport[] }).customers;
        return customers;
    }, [brandId, token, getCustomerListForFullExport]);

    const handleExportXlsx = useCallback(async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const customers = await fetchCustomersForExport();
            const flags = getBrandFlags();
            const optIns = brandOptIns ?? [];
            const headers = buildExportHeaders(flags, optIns);
            const rows = [headers, ...customers.map((c) => buildExportRow(c, flags, optIns))];

            const xlsx = new XlsxFile();
            xlsx.addWorksheet('customer', rows);

            const data = xlsx.toArrayBuffer();
            const brandName = brand?.name?.replace(/\s+/g, '_') ?? 'export';
            const filename = `Base_client_${brandName}.xlsx`;
            downloadFile(filename, data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        } catch (error) {
            console.error('Failed to export customers:', error);
        } finally {
            setIsExporting(false);
        }
    }, [isExporting, fetchCustomersForExport, brand, getBrandFlags, brandOptIns]);

    const handleExportCsv = useCallback(async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const customers = await fetchCustomersForExport();
            const flags = getBrandFlags();
            const optIns = brandOptIns ?? [];
            const headers = buildExportHeaders(flags, optIns);
            const rows = [headers, ...customers.map((c) => buildExportRow(c, flags, optIns))];

            const csvContent = rows
                .map((row) =>
                    row
                        .map((cell) => {
                            const cellStr = String(cell);
                            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                                return `"${cellStr.replace(/"/g, '""')}"`;
                            }
                            return cellStr;
                        })
                        .join(','),
                )
                .join('\n');

            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const brandName = brand?.name?.replace(/\s+/g, '_') ?? 'export';
            const filename = `Base_client_${brandName}.csv`;

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export customers:', error);
        } finally {
            setIsExporting(false);
        }
    }, [isExporting, fetchCustomersForExport, brand, getBrandFlags, brandOptIns]);

    return {
        onHideCreateModal: onHide,
        handleImport,
        handleExportXlsx,
        handleExportCsv,
        shouldUseGuestHierarchy,
        showImportModal,
        handleCloseImportModal,
        isExporting,
    };
};
